import { useState, useEffect, useCallback, useRef } from 'react';
import { toaster } from 'evergreen-ui';
import api from '../../../services/api';

export const useResultsData = (currentProject) => {
  const [summary, setSummary] = useState({
    counts: { services: 0, domains: 0, hosts: 0, webapps: 0, urls: 0, findings: 0 },
    new_counts: { services: 0, domains: 0, hosts: 0, webapps: 0, urls: 0, findings: 0 }
  });
  
  const [tabData, setTabData] = useState({
    services: { items: [], hasMore: true, loading: false, offset: 0 },
    domains: { items: [], hasMore: true, loading: false, offset: 0 },
    hosts: { items: [], hasMore: true, loading: false, offset: 0 },
    webapps: { items: [], hasMore: true, loading: false, offset: 0 },
    urls: { items: [], hasMore: true, loading: false, offset: 0 },
    findings: { items: [], hasMore: true, loading: false, offset: 0 }
  });
  
  const [commentCounts, setCommentCounts] = useState({});
  const [markedAsSeen, setMarkedAsSeen] = useState(new Set());
  const [initialLoading, setInitialLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  
  const isLoadingRef = useRef(false);

  const fetchSummary = useCallback(async () => {
    if (!currentProject?.id) return;
    
    try {
      const response = await api.get(`/projects/${currentProject.id}/results/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
      toaster.danger('Failed to fetch results summary');
    } finally {
      setInitialLoading(false);
    }
  }, [currentProject?.id]);

  const fetchCommentCounts = useCallback(async (tab, items) => {
    if (!currentProject?.id || !items || items.length === 0) return;

    const paramKey = {
      services: 'port_ids',
      domains: 'domain_ids',
      hosts: 'host_ids',
      findings: 'finding_ids',
    }[tab];
    const entityType = {
      services: 'port_id',
      domains: 'domain_id',
      hosts: 'host_id',
      findings: 'finding_id',
    }[tab];
    if (!paramKey) return;

    try {
      const ids = items.map(i => i.id).join(',');
      const response = await api.get(`/projects/${currentProject.id}/comments/counts`, {
        params: { [paramKey]: ids },
      });
      const rawCounts = response.data.counts || {};
      const counts = {};
      for (const item of items) {
        const key = `${entityType}_${item.id}`;
        counts[key] = rawCounts[item.id] || 0;
      }
      setCommentCounts(prev => ({ ...prev, ...counts }));
    } catch (error) {
      console.error('Error fetching comment counts:', error);
    }
  }, [currentProject?.id]);

  const fetchTabData = useCallback(async (tab, offset = 0, isLoadMore = false) => {
    if (!currentProject?.id || isLoadingRef.current) return;
    
    isLoadingRef.current = true;
    
    setTabData(prev => ({
      ...prev,
      [tab]: { ...prev[tab], loading: true }
    }));

    try {
      const response = await api.get(`/projects/${currentProject.id}/results/${tab}`, {
        params: { offset, limit: 50 }
      });
      
      let newItems = [];
      if (response.data && typeof response.data === 'object') {
        if (response.data[tab] && Array.isArray(response.data[tab])) {
          newItems = response.data[tab];
        } else {
          console.warn(`Tab ${tab} not found in response:`, response.data);
          newItems = [];
        }
      }
      
      if (!Array.isArray(newItems)) {
        console.warn(`Expected array for ${tab}, got:`, typeof newItems, newItems);
        newItems = [];
      }
      
      setTabData(prev => ({
        ...prev,
        [tab]: {
          items: isLoadMore ? [...prev[tab].items, ...newItems] : newItems,
          hasMore: response.data.has_more || false,
          loading: false,
          offset: offset + newItems.length
        }
      }));
      
      if (newItems.length > 0) {
        fetchCommentCounts(tab, newItems);
      }
    } catch (error) {
      console.error(`Error fetching ${tab}:`, error);
      toaster.danger(`Failed to fetch ${tab}`);
      setTabData(prev => ({
        ...prev,
        [tab]: { ...prev[tab], loading: false }
      }));
    } finally {
      isLoadingRef.current = false;
    }
  }, [currentProject?.id, fetchCommentCounts]);

  const markAsSeen = useCallback(async (entityType, entityId) => {
    if (!currentProject?.id || !entityId) return;
    
    const seenKey = `${entityType}_${entityId}`;
    if (markedAsSeen.has(seenKey)) {
      return;
    }
    
    const entityTypeMap = {
      'host': 'hosts',
      'domain': 'domains',
      'port': 'ports',
      'service': 'ports',
      'url': 'urls',
      'finding': 'findings'
    };
    
    const apiEntityType = entityTypeMap[entityType] || entityType + 's';
    
    try {
      await api.post(`/projects/${currentProject.id}/results/${apiEntityType}/${entityId}/mark-seen`);
      
      setMarkedAsSeen(prev => new Set([...prev, seenKey]));
      
      const tabMap = {
        'service': 'services',
        'port': 'services',
        'domain': 'domains', 
        'host': 'hosts',
        'url': 'webapps',
        'finding': 'findings'
      };
      
      const targetTab = tabMap[entityType] || 'services';
      
      setTabData(prev => ({
        ...prev,
        [targetTab]: {
          ...prev[targetTab],
          items: prev[targetTab].items.map(item => {
            if (item.id === entityId) {
              return { ...item, is_new: false };
            }
            if (entityType === 'host' && item.ports) {
              item.ports = item.ports.map(port => 
                port.id === entityId ? { ...port, is_new: false } : port
              );
            }
            if (entityType === 'host' && item.findings) {
              item.findings = item.findings.map(finding => 
                finding.id === entityId ? { ...finding, is_new: false } : finding
              );
            }
            if (entityType === 'domain' && item.findings) {
              item.findings = item.findings.map(finding => 
                finding.id === entityId ? { ...finding, is_new: false } : finding
              );
            }
            return item;
          })
        }
      }));
      
      fetchSummary();
    } catch (error) {
      console.error(`Error marking ${entityType} as seen:`, error);
    }
  }, [currentProject?.id, fetchSummary, markedAsSeen]);

  const handleAction = async (actionType, itemType, itemId, extraParams = {}) => {
    const actionKey = `${actionType}_${itemType}_${itemId}`;
    setActionLoading(prev => ({ ...prev, [actionKey]: true }));
    
    try {
      let endpoint = '';
      let method = 'POST';
      let successMessage = '';
      
      switch (actionType) {
        case 'screenshot':
          endpoint = `/tasks/${currentProject.id}/screenshot`;
          method = 'POST';
          successMessage = 'Screenshot task started';
          break;
        case 'ffuf':
          endpoint = `/tasks/${currentProject.id}/ffuf`;
          method = 'POST';
          successMessage = 'FFUF task started';
          break;
        case 'nmap':
          endpoint = `/tasks/${currentProject.id}/nmap`;
          method = 'POST';
          successMessage = 'Nmap task started';
          break;
        case 'nuclei':
          endpoint = `/tasks/${currentProject.id}/nuclei`;
          method = 'POST';
          successMessage = 'Nuclei task started';
          break;
        case 'mark_checked':
          endpoint = `/projects/${currentProject.id}/results/${itemType}s/${itemId}/mark-checked`;
          method = 'POST';
          successMessage = 'Marked as checked';
          break;
        case 'delete':
          endpoint = `/projects/${currentProject.id}/results/${itemType}s/${itemId}`;
          method = 'DELETE';
          successMessage = `${itemType.charAt(0).toUpperCase() + itemType.slice(1)} deleted`;
          break;
        default:
          throw new Error(`Unknown action: ${actionType}`);
      }
      
      const payload = { ...extraParams };
      if (itemType === 'domain') payload.domain = extraParams.name;
      if (itemType === 'host') payload.target = extraParams.ip;
      if (itemType === 'service') payload.target = `${extraParams.ip}:${extraParams.port}`;
      if (itemType === 'url') payload.url = extraParams.url;

      const response = await api.request({
        method,
        url: endpoint,
        data: payload
      });

      // For mark_checked, use the response message and is_checked value
      if (actionType === 'mark_checked') {
        const message = response.data?.message || successMessage;
        toaster.success(message);

        const tabMap = {
          'service': 'services',
          'domain': 'domains',
          'host': 'hosts',
          'url': 'webapps',
          'finding': 'findings'
        };

        const targetTab = tabMap[itemType];

        if (targetTab && response.data) {
          const newIsChecked = response.data.is_checked;

          setTabData(prev => {
            const updatedItems = prev[targetTab].items.map(item => {
              if (item.id === itemId) {
                return { ...item, is_checked: newIsChecked };
              }
              return item;
            });
            return {
              ...prev,
              [targetTab]: {
                ...prev[targetTab],
                items: updatedItems
              }
            };
          });
        }
      } else if (actionType === 'delete') {
        toaster.success(successMessage);

        const tabMap = {
          'service': 'services',
          'domain': 'domains',
          'host': 'hosts',
          'url': 'webapps',
          'finding': 'findings'
        };
        const targetTab = tabMap[itemType];
        if (targetTab) {
          setTabData(prev => ({
            ...prev,
            [targetTab]: {
              ...prev[targetTab],
              items: prev[targetTab].items.filter(item => item.id !== itemId)
            }
          }));
        }
        fetchSummary();
      } else {
        toaster.success(successMessage);
      }

    } catch (error) {
      console.error(`Error executing ${actionType}:`, error);
      toaster.danger(`Failed to execute ${actionType}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  useEffect(() => {
    if (currentProject?.id) {
      fetchSummary();
      fetchTabData('services', 0, false);
    }
  }, [currentProject?.id, fetchSummary, fetchTabData]);

  return {
    summary,
    tabData,
    setTabData,
    commentCounts,
    markedAsSeen,
    initialLoading,
    actionLoading,
    fetchSummary,
    fetchTabData,
    markAsSeen,
    handleAction,
    isLoadingRef
  };
};

